// Generadores de JSON-LD (schema.org) para SEO.
// Fuente única de verdad: src/mock.js (locations + businessInfo).
// NO incluimos aggregateRating de reseñas propias: Google penaliza el self-serving
// review markup. Las reseñas viven en el perfil de Google Business.

import { locations, businessInfo } from '../mock';

export const SITE_URL = 'https://brazolovers.com';

const phoneE164 = (raw) => {
  const digits = String(raw || '').replace(/[^\d]/g, '');
  return digits ? `+${digits}` : undefined;
};

const sameAs = [businessInfo.social?.instagram, businessInfo.social?.facebook].filter(Boolean);

const DAY_MAP = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

// Convierte workingHours { monday: {open, close}|null, ... } a openingHoursSpecification,
// agrupando días consecutivos con el mismo horario en una sola entrada.
const buildOpeningHours = (workingHours = {}) => {
  const specs = [];
  for (const [key, day] of Object.entries(DAY_MAP)) {
    const wh = workingHours[key];
    if (!wh || !wh.open || !wh.close) continue;
    const last = specs[specs.length - 1];
    if (last && last.opens === wh.open && last.closes === wh.close) {
      last.dayOfWeek.push(day);
    } else {
      specs.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [day],
        opens: wh.open,
        closes: wh.close,
      });
    }
  }
  return specs;
};

// Un LocalBusiness/Bakery por cada local físico.
export const getLocalBusinessJsonLd = () =>
  locations.map((loc) => {
    const seo = loc.seo || {};
    const node = {
      '@context': 'https://schema.org',
      '@type': 'Bakery',
      '@id': `${SITE_URL}/ubicaciones#local-${loc.id}`,
      name: seo.branchName || `${businessInfo.name} — ${loc.name}`,
      image: `${SITE_URL}/og-image.jpg`,
      url: `${SITE_URL}/ubicaciones/`,
      telephone: phoneE164(loc.phone),
      email: businessInfo.email,
      priceRange: '$$',
      servesCuisine: 'Pastelería',
      currenciesAccepted: 'USD',
      address: {
        '@type': 'PostalAddress',
        streetAddress: seo.streetAddress || loc.address,
        addressLocality: seo.addressLocality || 'Quito',
        addressRegion: seo.addressRegion || 'Pichincha',
        addressCountry: seo.addressCountry || 'EC',
      },
      openingHoursSpecification: buildOpeningHours(loc.workingHours),
      sameAs,
    };
    if (seo.postalCode) node.address.postalCode = seo.postalCode;
    if (seo.geo) {
      node.geo = {
        '@type': 'GeoCoordinates',
        latitude: seo.geo.lat,
        longitude: seo.geo.lng,
      };
    }
    if (loc.googleMapsUrl) node.hasMap = loc.googleMapsUrl;
    return node;
  });

// Organización (marca paraguas) — útil en la home.
export const getOrganizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: businessInfo.name,
  url: `${SITE_URL}/`,
  logo: `${SITE_URL}/assets/logo.png`,
  description: businessInfo.description,
  sameAs,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: phoneE164(businessInfo.phone),
    contactType: 'customer service',
    email: businessInfo.email,
    areaServed: 'EC',
    availableLanguage: ['Spanish'],
  },
});

// WebSite (refuerza la marca; publisher apunta a la Organización).
export const getWebSiteJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: `${SITE_URL}/`,
  name: businessInfo.name,
  inLanguage: 'es-EC',
  publisher: { '@id': `${SITE_URL}/#organization` },
});

// Preguntas frecuentes: fuente única para la sección visible (FaqSection) y el
// JSON-LD FAQPage. Contenido real, local y con keywords long-tail.
export const FAQ_ITEMS = [
  {
    q: '¿Cómo hago un pedido en Brazolovers?',
    a: 'Puedes pedir en línea desde nuestra página principal o escribirnos por WhatsApp al +593 98 481 4807. Eliges retiro en tienda en cualquiera de nuestros dos locales en Quito o entrega a domicilio.',
  },
  {
    q: '¿Hacen entregas a domicilio? ¿Cuánto cuesta el envío?',
    a: 'Sí, hacemos entregas a domicilio en Quito. La tarifa es de $2.00 hasta 4 km y $0.50 por kilómetro adicional, hasta un máximo de 10 km. El retiro en tienda no tiene costo.',
  },
  {
    q: '¿Cuáles son los horarios de atención?',
    a: 'Matriz (Sergio Játiva): lunes a sábado de 08:30 a 19:00; domingos cerrado. Sucursal (Eloy Alfaro y Rusia): lunes a viernes de 10:00 a 19:00, sábados de 09:00 a 18:00 y domingos de 09:00 a 13:00. Los domingos solo atiende la Sucursal.',
  },
  {
    q: '¿Dónde están ubicados?',
    a: 'Tenemos dos locales en Quito: la Matriz en Sergio Játiva 175-147 y la Sucursal en Av. Eloy Alfaro N33-21 y Rusia (Edificio Niza).',
  },
  {
    q: '¿Cuántos sabores de brazos gitanos tienen?',
    a: 'Más de 40 sabores únicos de brazos gitanos artesanales, además de pasteles, galletas, panadería, desayunos y bebidas.',
  },
  {
    q: '¿Necesito pedir con anticipación?',
    a: 'Los productos están sujetos a disponibilidad y algunos pasteles personalizados (como La Reina) requieren al menos 24 horas de anticipación. Para pedidos grandes te recomendamos avisarnos con tiempo.',
  },
];

export const getFaqJsonLd = (items = FAQ_ITEMS) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((it) => ({
    '@type': 'Question',
    name: it.q,
    acceptedAnswer: { '@type': 'Answer', text: it.a },
  })),
});

// Breadcrumbs reutilizable por página.
export const getBreadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});
